/**
 * Quantidade de dígitos (números) no número do processo.
 * @constant {number}
 * @readonly
 */
export const QTD_DIGITOS_NUMERO_PROCESSO = 20;

/**
 * Tamanhos de cada campo que compõe o número do processo.
 * @constant {object}
 * @readonly
 */
// prettier-ignore
export const TAMANHOS_CAMPOS_NUMERO_PROCESSO = {
    numero:   { minimo: 1, maximo: 7 },
    digito:   { minimo: 2, maximo: 2 },
    ano:      { minimo: 4, maximo: 4 },
    orgao:    { minimo: 1, maximo: 1 },
    regional: { minimo: 1, maximo: 2 },
    vara:     { minimo: 1, maximo: 4 }
};

function carregarRegExpNumericaDinamica(objMinMax) {
    if (objMinMax?.minimo === undefined || objMinMax?.maximo === undefined) {
        return /\d/;
    }

    if (objMinMax.minimo === objMinMax.maximo) {
        if (objMinMax.minimo === 1) {
            return /\d/;
        }

        return RegExp(`\\d{${objMinMax.minimo}}`);
    }

    return new RegExp(`\\d{${objMinMax.minimo},${objMinMax.maximo}}`);
}

const PROCESSO_NUMERO = carregarRegExpNumericaDinamica(TAMANHOS_CAMPOS_NUMERO_PROCESSO.numero);
const PROCESSO_DIGITO = carregarRegExpNumericaDinamica(TAMANHOS_CAMPOS_NUMERO_PROCESSO.digito);
const PROCESSO_ANO = carregarRegExpNumericaDinamica(TAMANHOS_CAMPOS_NUMERO_PROCESSO.ano);
const PROCESSO_SEGMENTO_JUD = carregarRegExpNumericaDinamica(TAMANHOS_CAMPOS_NUMERO_PROCESSO.orgao);
const PROCESSO_TRIBUNAL = carregarRegExpNumericaDinamica(TAMANHOS_CAMPOS_NUMERO_PROCESSO.regional);
const PROCESSO_ORIGEM = carregarRegExpNumericaDinamica(TAMANHOS_CAMPOS_NUMERO_PROCESSO.vara);
const PROCESSO_SEGMENTO_COM_TRIBUNAL = /\d{3}/;

/**
 * const TIPO_ESTRUTURA_NUMERO_PROCESSO = {
 *     GENERICA: /^(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(((?<j>\d)\.(?<tr>\d{1,2}))|(?<jtr>\d{3})){1}(\.(?<origem>\d{1,4}))?$/, // NNNNNNN-DD.AAAA.J.TR.OOOO ou NNNNNNN-DD.AAAA.JTR.OOOO, onde ".OOOO" é opcional
 *     COMPLETA: /^(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(?<j>\d)\.(?<tr>\d{1,2})\.(?<origem>\d{1,4})$/, // NNNNNNN-DD.AAAA.J.TR.OOOO
 *     COMPLETA_SEM_SEPARACAO_JTR: /^(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(?<jtr>\d{3})\.(?<origem>\d{1,4})$/, // NNNNNNN-DD.AAAA.JTR.OOOO
 *     SEM_ORIGEM_COM_SEPARACAO_JTR: /^(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(?<j>\d)\.(?<tr>\d{1,2})$/, // NNNNNNN-DD.AAAA.J.TR
 *     SEM_ORIGEM_SEM_SEPARACAO_JTR: /^(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(?<jtr>\d{3})$/, // NNNNNNN-DD.AAAA.JTR
 *     APENAS_NUMEROS: /^\d{20}$/ // NNNNNNNDDAAAAJTROOOO
 * };
 *
 * @desc Tipos de estrutura para números de processos válidos, conforme definido na RESOLUÇÃO Nº 65, DE 16 DE DEZEMBRO DE 2008, atualizada pelas resoluções nº 223/2016 e nº 477/2022.
 * @see {@link https://atos.cnj.jus.br/atos/detalhar/119}
 * @constant {Object.<string, string>}
 * @readonly
 */
export const TIPO_ESTRUTURA_NUMERO_PROCESSO = {
    GENERICA: new RegExp(
        `^(?<numero>${PROCESSO_NUMERO.source})-(?<digito>${PROCESSO_DIGITO.source})\\.(?<ano>${PROCESSO_ANO.source})\\.(((?<j>${PROCESSO_SEGMENTO_JUD.source})\\.(?<tr>${PROCESSO_TRIBUNAL.source}))|(?<jtr>${PROCESSO_SEGMENTO_COM_TRIBUNAL.source})){1}(\\.(?<origem>${PROCESSO_ORIGEM.source}))?$`
    ),
    COMPLETA: new RegExp(
        `^(?<numero>${PROCESSO_NUMERO.source})-(?<digito>${PROCESSO_DIGITO.source})\\.(?<ano>${PROCESSO_ANO.source})\\.(?<j>${PROCESSO_SEGMENTO_JUD.source})\\.(?<tr>${PROCESSO_TRIBUNAL.source})\\.(?<origem>${PROCESSO_ORIGEM.source})$`
    ),
    APENAS_NUMEROS: new RegExp(`^(?<apenas_numeros>\\d{${QTD_DIGITOS_NUMERO_PROCESSO}})$`)
};

/**
 * Expressão regular para formatar (aplicar máscara completa) ao número do processo que tenha apenas números.
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_NUMERO_PROCESSO_APENAS_NUMEROS_AGRUPAMENTOS = /^(\d{7})(\d{2})(\d{4})(\d)(\d{2})(\d{4})$/;

/**
 * Separador de classe processual e número do processo.
 * @constant {string}
 * @readonly
 */
export const SEPARADOR_CLASSE_NUMERO_PADRAO = ' - ';

/**
 * Expressão regular para identificar um separador entre classe e número do processo (hífen, meia-risca ou travessão com espaços opcionais antes e depois).
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_SEPARADOR_CLASSE_E_NUMERO = / {0,4}[-–—] {0,4}| /;

/**
 * Expressão regular para a classe processual.
 * Apenas letras e vírgulas, espaços ou hífens (desde que não termine com espaço ou hífen).
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_CLASSE_PROCESSUAL = /[A-Za-z]+(([ -]|(, ?))[A-Za-z]+)*/;

/**
 * Expressão regular para a classe processual.
 * Apenas letras, espaços ou hífens (desde que não termine com hífen), seguido ou não do separador.
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_CLASSE_PROCESSUAL_COM_SEPARADOR_OPCIONAL = new RegExp(
    `^(?<classe>${ESTRUTURA_CLASSE_PROCESSUAL.source})(?<separador>${ESTRUTURA_SEPARADOR_CLASSE_E_NUMERO.source})?$`
);

/**
 * Expressão regular para a identificação completa do processo (classe processual + número do processo com qualquer formatação válida).
 * /^(?<classe>[A-Za-z]+(([ -]|(, ?))[A-Za-z]+)*)(?<separador> {0,4}[-–—] {0,4}| )(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(((?<j>\d)\.(?<tr>\d{1,2}))|(?<jtr>\d{3})){1}(\.(?<origem>\d{1,4}))?$/
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_NUMERO_PROCESSO_COM_CLASSE_PROCESSUAL = new RegExp(
    `^(?<classe>${ESTRUTURA_CLASSE_PROCESSUAL.source})(?<separador>${ESTRUTURA_SEPARADOR_CLASSE_E_NUMERO.source})(?<numero>${PROCESSO_NUMERO.source})-(?<digito>${PROCESSO_DIGITO.source})\\.(?<ano>${PROCESSO_ANO.source})\\.(((?<j>${PROCESSO_SEGMENTO_JUD.source})\\.(?<tr>${PROCESSO_TRIBUNAL.source}))|(?<jtr>${PROCESSO_SEGMENTO_COM_TRIBUNAL.source})){1}(\\.(?<origem>${PROCESSO_ORIGEM.source}))?$`
);

/**
 * Expressão regular para a identificação completa do processo [classe processual (opcional) + número do processo com qualquer formatação válida].
 * /^((?<classe>[A-Za-z]+(([ -]|(, ?))[A-Za-z]+)*)(?<separador> {0,4}[-–—] {0,4}| ))?(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(((?<j>\d)\.(?<tr>\d{1,2}))|(?<jtr>\d{3})){1}(\.(?<origem>\d{1,4}))?$/;
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_NUMERO_PROCESSO_COM_OU_SEM_CLASSE_PROCESSUAL =
    /^((?<classe>[A-Za-z]+(([ -]|(, ?))[A-Za-z]+)*)(?<separador> {0,4}[-–—] {0,4}| ))?(?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(((?<j>\d)\.(?<tr>\d{1,2}))|(?<jtr>\d{3})){1}(\.(?<origem>\d{1,4}))?$/;

/**
 * Expressão regular para a identificação completa do processo [classe processual (opcional) + número do processo com qualquer formatação válida] ou o número do processo sem qualquer formatação (apenas dígitos).
 * @constant {string}
 * @readonly
 */
export const ESTRUTURA_NUMERO_PROCESSO_COM_OU_SEM_CLASSE_PROCESSUAL_OU_APENAS_NUMEROS =
    /^(((?<classe>[A-Za-z]+(([ -]|(, ?))[A-Za-z]+)*)(?<separador> {0,4}[-–—] {0,4}| ))?(((?<numero>\d{1,7})-(?<digito>\d{2})\.(?<ano>\d{4})\.(((?<j>\d)\.(?<tr>\d{1,2}))|(?<jtr>\d{3})){1}(\.(?<origem>\d{1,4}))?)|(?<apenas_numeros>\d{20})){1})$|^(?<apenas_numeros_sem_classe>\d{20})$/;

/**
 * @classdesc Número do processo.
 */
export class NumeroProcesso {
    constructor(classe, numero, digito, ano, orgaoOuSegmento, tribunal, origem) {
        this.classe = classe || '';
        this.numero = numero || '';
        this.digito = digito || '';
        this.ano = ano || '';
        this.orgao = orgaoOuSegmento || '';
        this.regional = tribunal || '';
        this.vara = origem || '';
    }

    toString() {
        return `${this.classe}${SEPARADOR_CLASSE_NUMERO_PADRAO}${this.numero}-${this.digito}.${this.ano}.${this.orgao}.${this.regional}.${this.vara}`;
    }
}

/**
 * @classdesc Configuração para pesquisa de números de processos.
 */
export class ConfiguracaoPesquisaNumeroProcesso {
    constructor(ignorarClasseProcessual = true, ignorarCamposVazios = false, buscarParteDeUmValor = false) {
        this.ignorarClasseProcessual = ignorarClasseProcessual;
        this.ignorarCamposVazios = ignorarCamposVazios;
        this.buscarParteDeUmValor = buscarParteDeUmValor;
    }

    toString() {
        return `ignorarClasseProcessual=${this.ignorarClasseProcessual}; ignorarCamposVazios=${this.ignorarCamposVazios}; buscarParteDeUmValor=${this.buscarParteDeUmValor}`;
    }
}

export default class NumeroProcessoUtil {
    constructor() {
        throw new Error('Deve existir apenas uma instância.');
    }

    /**
     * Verifica se a classe processual é válida ou não.
     *
     * @param {string|Object} numeroCompletoProcessoOuClasse O número do processo completo (classe + número do processo) ou apenas a classe ou o objeto com o atributo "classe"
     * @returns {boolean} Um sinalizador que indica se a classe processual é válida (true) ou não (false).
     */
    static validarClasseProcessual(numeroCompletoProcessoOuClasse) {
        let classe = NumeroProcessoUtil.#tratarParametroNumeroProcesso(numeroCompletoProcessoOuClasse);

        if (typeof numeroCompletoProcessoOuClasse === 'object') {
            // prettier-ignore
            classe = (numeroCompletoProcessoOuClasse.classe ? numeroCompletoProcessoOuClasse.classe : '');

            if (classe === '') {
                return false;
            }
        }

        const primeiraOcorrenciaNumero = classe.search(/\d/);

        if (primeiraOcorrenciaNumero === 0) {
            return false;
        } else if (primeiraOcorrenciaNumero === -1) {
            return ESTRUTURA_CLASSE_PROCESSUAL_COM_SEPARADOR_OPCIONAL.test(classe);
        } else {
            return ESTRUTURA_CLASSE_PROCESSUAL_COM_SEPARADOR_OPCIONAL.test(
                classe.substring(0, primeiraOcorrenciaNumero)
            );
        }
    }

    /**
     * Verifica se o número do processo é válido ou não.
     *
     * @param {string|Object} numeroCompletoProcesso O número do processo completo (classe + número do processo) ou apenas o número do processo ou um objeto com os campos do processo.
     * @returns {boolean} Indicador se o número do processo é válido (true) ou não (false).
     */
    static validarNumeroProcesso(numeroCompletoProcesso) {
        const TAMANHO_MINIMO_NUMERO_FORMATADO = 13; // N-DD.AAAA.JTR
        const TAMANHO_MAXIMO_NUMERO_FORMATADO = 25; // NNNNNNN-DD.AAAA.J.TR.OOOO
        const numeroProcesso = NumeroProcessoUtil.removerClasseProcessual(numeroCompletoProcesso);

        if (
            numeroProcesso.length < TAMANHO_MINIMO_NUMERO_FORMATADO ||
            numeroProcesso.length > TAMANHO_MAXIMO_NUMERO_FORMATADO
        ) {
            return false;
        }

        if (
            numeroProcesso.length === QTD_DIGITOS_NUMERO_PROCESSO &&
            TIPO_ESTRUTURA_NUMERO_PROCESSO.APENAS_NUMEROS.test(numeroProcesso)
        ) {
            return true;
        }

        return TIPO_ESTRUTURA_NUMERO_PROCESSO.GENERICA.test(numeroProcesso);
    }

    /**
     * Extrai a classe processual de um possível número completo de processo.
     *
     * @param {string|Object} numeroCompletoProcessoOuClasse O número do processo completo (classe + número do processo) ou apenas a classe ou o objeto com o atributo "classe".
     * @returns {string} A classe processual.
     */
    static getClasseProcessual(numeroCompletoProcessoOuClasse) {
        let classe = '';

        if (typeof numeroCompletoProcessoOuClasse === 'object') {
            classe = numeroCompletoProcessoOuClasse.classe ? numeroCompletoProcessoOuClasse.classe : '';

            if (classe === '') {
                return classe;
            }
        }

        numeroCompletoProcessoOuClasse =
            NumeroProcessoUtil.#tratarParametroNumeroProcesso(numeroCompletoProcessoOuClasse);
        const primeiraOcorrenciaNumero = numeroCompletoProcessoOuClasse.search(/\d/);

        if (primeiraOcorrenciaNumero === 0) {
            return '';
        }

        let resultado;

        if (primeiraOcorrenciaNumero === -1) {
            resultado = ESTRUTURA_CLASSE_PROCESSUAL_COM_SEPARADOR_OPCIONAL.exec(numeroCompletoProcessoOuClasse);
        } else {
            resultado = ESTRUTURA_CLASSE_PROCESSUAL_COM_SEPARADOR_OPCIONAL.exec(
                numeroCompletoProcessoOuClasse.substring(0, primeiraOcorrenciaNumero)
            );
        }

        return resultado?.groups?.classe || '';
    }

    /**
     * Remove a classe processual de um número completo de processo (classe + número do processo).
     *
     * @param {string|Object} numeroCompletoProcesso O número completo do processo (classe + número do processo) ou uma instância de NumeroProcesso ou um objeto que contenha os mesmos atributos.
     * @returns {string} O número contido no parâmetro informado, sem alterá-lo.
     */
    static removerClasseProcessual(numeroCompletoProcesso) {
        numeroCompletoProcesso = NumeroProcessoUtil.#tratarParametroNumeroProcesso(numeroCompletoProcesso);
        const primeiraOcorrenciaNumero = numeroCompletoProcesso.search(/\d/);

        if (primeiraOcorrenciaNumero === -1) {
            console.warn(`Processo sem número: ${JSON.stringify(numeroCompletoProcesso)}`);
            return '';
        }

        return numeroCompletoProcesso.substring(primeiraOcorrenciaNumero);
    }

    /**
     * Aplica a máscara ao número do processo.
     *
     * @param {string|Object} numeroCompletoProcesso O número do processo completo (classe + número do processo) ou uma instância de NumeroProcesso ou um objeto que contenha os mesmos atributos.
     * @param {boolean} comClasseProcessual Indica se a classe processual deverá ser incluída ou não.
     * @param {boolean} completarComZerosEsquerda Indica se o número deverá ser completado com zeros à esquerda.
     * @returns {string} O número do processo contido no parâmetro informado, com a máscara (NNNNNNN-DD.AAAA.J.TR.OOOO) aplicada (os zeros à esquerda dos campos tribunal e origem são automaticamente aplicados).
     */
    static formatarNumeroProcesso(
        numeroCompletoProcesso,
        comClasseProcessual = false,
        completarComZerosEsquerda = false
    ) {
        const numeroProcesso = NumeroProcessoUtil.#tratarParametroNumeroProcesso(numeroCompletoProcesso);
        let numeroProcessoObj;

        if (typeof numeroProcesso === 'object') {
            numeroProcessoObj = numeroCompletoProcesso;
        } else if (typeof numeroProcesso === 'string') {
            if (ESTRUTURA_NUMERO_PROCESSO_COM_OU_SEM_CLASSE_PROCESSUAL.test(numeroProcesso)) {
                numeroProcessoObj = this.getObjetoNumeroProcesso(numeroProcesso);
            } else if (TIPO_ESTRUTURA_NUMERO_PROCESSO.APENAS_NUMEROS.test(numeroProcesso)) {
                return NumeroProcessoUtil.formatarNumeroProcessoApenasNumeros(numeroProcesso);
            } else {
                console.warn(`Número de processo inválido: ${JSON.stringify(numeroCompletoProcesso)}`);
                return numeroCompletoProcesso;
            }
        }

        return NumeroProcessoUtil.formatarNumeroProcessoObj(
            numeroProcessoObj,
            comClasseProcessual,
            completarComZerosEsquerda
        );
    }

    /**
     * Aplica a máscara ao número do processo.
     *
     * @param {Object} numeroProcessoObj Uma instância de NumeroProcesso ou um objeto que contenha os mesmos atributos.
     * @param {boolean} comClasseProcessual Indica se a máscara deverá considerar ou não a máscara.
     * @param {boolean} completarComZerosEsquerda Indica se o número deverá ser completado com zeros à esquerda.
     * @returns {string} O número do processo contido no parâmetro informado, com a máscara (NNNNNNN-DD.AAAA.J.TR.OOOO) aplicada (os zeros à esquerda dos campos tribunal e origem são automaticamente aplicados).
     */
    static formatarNumeroProcessoObj(
        numeroProcessoObj,
        comClasseProcessual = false,
        completarComZerosEsquerda = false
    ) {
        let numeroFormatado = '';

        if (comClasseProcessual) {
            numeroFormatado += numeroProcessoObj.classe + SEPARADOR_CLASSE_NUMERO_PADRAO;
        }

        if (completarComZerosEsquerda) {
            numeroProcessoObj.numero = NumeroProcessoUtil.completarZerosEsquerda(
                numeroProcessoObj.numero,
                TAMANHOS_CAMPOS_NUMERO_PROCESSO.numero.maximo
            );
        }

        numeroFormatado += `${numeroProcessoObj.numero}-${numeroProcessoObj.digito}.${numeroProcessoObj.ano}.${numeroProcessoObj.orgao}.${numeroProcessoObj.regional}.${numeroProcessoObj.vara}`;

        return numeroFormatado;
    }

    /**
     * Aplica a máscara ao número do processo.
     *
     * @param {number|bigint|string} processoApenasNumeros Processo apenas com dígitos.
     * @returns {string} O número do processo contido no parâmetro informado, com a máscara (NNNNNNN-DD.AAAA.J.TR.OOOO) aplicada (os zeros à esquerda dos campos tribunal e origem são automaticamente aplicados).
     */
    static formatarNumeroProcessoApenasNumeros(processoApenasNumeros) {
        if (
            typeof processoApenasNumeros !== 'string' &&
            typeof processoApenasNumeros !== 'number' &&
            typeof processoApenasNumeros !== 'bigint'
        ) {
            throw new TypeError(
                `Parâmetro inválido para \`formatarNumeroProcessoApenasNumeros\`. Esperado um número ou uma string numérica.`
            );
        }

        let processoStr = '' + processoApenasNumeros;

        if (processoStr.length === QTD_DIGITOS_NUMERO_PROCESSO) {
            return processoStr.replace(ESTRUTURA_NUMERO_PROCESSO_APENAS_NUMEROS_AGRUPAMENTOS, '$1-$2.$3.$4.$5.$6');
        }

        console.warn(
            `Parâmetro inválido para \`formatarNumeroProcessoApenasNumeros\`. Esperado um número ou uma string numérica com ${QTD_DIGITOS_NUMERO_PROCESSO} dígitos.`
        );
        return processoStr;
    }

    /**
     * Compara números de processos.
     *
     * @param {string | Object} numeroCompletoProcesso O número do processo completo (classe + número do processo) ou uma instância de NumeroProcesso ou um objeto que contenha os mesmos atributos.
     * @param {string | Object} numeroPesquisa O número de processo a ser pesquisado.
     * @param {Object} configuracaoPesquisa Configuração de pesquisa.
     * @returns {boolean} Um sinalizador que indica se a comparação foi válida (true) ou não (false).
     */
    static compararNumeroProcesso(numeroCompletoProcesso, numeroPesquisa, configuracaoPesquisa = null) {
        let numeroProcessoObj;

        if (configuracaoPesquisa === null) {
            configuracaoPesquisa = new ConfiguracaoPesquisaNumeroProcesso();
        }

        const completarOrigem = !configuracaoPesquisa.ignorarCamposVazios;
        numeroProcessoObj = NumeroProcessoUtil.getObjetoNumeroProcesso(numeroCompletoProcesso, completarOrigem);

        if (typeof numeroPesquisa === 'string' || numeroPesquisa instanceof String) {
            numeroPesquisa = NumeroProcessoUtil.getObjetoNumeroProcesso(numeroPesquisa.valueOf(), completarOrigem);
        }

        let retorno = false;

        if (numeroPesquisa && numeroProcessoObj) {
            retorno = NumeroProcessoUtil.#compararCampos(configuracaoPesquisa, numeroPesquisa, numeroProcessoObj);
        }

        return retorno;
    }

    // prettier-ignore
    static #compararCampos(configuracaoPesquisa, numeroPesquisa, numeroProcessoObj) {
        let retorno = true;

        retorno = retorno && (configuracaoPesquisa.ignorarClasseProcessual || numeroPesquisa.classe === numeroProcessoObj.classe);
        retorno = retorno && NumeroProcessoUtil.#compararNumeros(numeroPesquisa, numeroProcessoObj, configuracaoPesquisa);
        retorno = retorno && NumeroProcessoUtil.#compararDigitos(numeroPesquisa, numeroProcessoObj, configuracaoPesquisa);
        retorno = retorno && NumeroProcessoUtil.#compararAnos(numeroPesquisa, numeroProcessoObj, configuracaoPesquisa);
        retorno = retorno && NumeroProcessoUtil.#compararOrgaos(numeroPesquisa, numeroProcessoObj, configuracaoPesquisa);
        retorno = retorno && NumeroProcessoUtil.#compararTribunais(numeroPesquisa, numeroProcessoObj, configuracaoPesquisa);
        retorno = retorno && NumeroProcessoUtil.#compararOrigens(numeroPesquisa, numeroProcessoObj, configuracaoPesquisa);

        return retorno;
    }

    static #compararCampo(numeroPesquisaObj, numeroProcessoObj, configuracaoPesquisa, campo, tamanhoCampo) {
        if (configuracaoPesquisa.ignorarCamposVazios && (!numeroPesquisaObj[campo] || !numeroProcessoObj[campo])) {
            return true;
        }

        let valorComZeros;

        if (numeroProcessoObj[campo]) {
            valorComZeros = NumeroProcessoUtil.completarZerosEsquerda(numeroProcessoObj[campo], tamanhoCampo);
        }

        if (!valorComZeros) {
            return false;
        }

        if (configuracaoPesquisa.buscarParteDeUmValor) {
            return !!(numeroPesquisaObj[campo] && valorComZeros.indexOf(numeroPesquisaObj[campo]) !== -1);
        } else {
            let valorPesquisaComZeros;

            if (numeroPesquisaObj[campo]) {
                valorPesquisaComZeros = NumeroProcessoUtil.completarZerosEsquerda(
                    numeroPesquisaObj[campo],
                    tamanhoCampo
                );
            }

            return !!(valorPesquisaComZeros && valorPesquisaComZeros === valorComZeros);
        }
    }

    static #compararNumeros(numeroPesquisa, numeroProcesso, configuracaoPesquisa) {
        const campo = 'numero';
        return NumeroProcessoUtil.#compararCampo(
            numeroPesquisa,
            numeroProcesso,
            configuracaoPesquisa,
            campo,
            TAMANHOS_CAMPOS_NUMERO_PROCESSO[campo].maximo
        );
    }

    static #compararDigitos(numeroPesquisa, numeroProcesso, configuracaoPesquisa) {
        const campo = 'digito';
        return NumeroProcessoUtil.#compararCampo(
            numeroPesquisa,
            numeroProcesso,
            configuracaoPesquisa,
            campo,
            TAMANHOS_CAMPOS_NUMERO_PROCESSO[campo].maximo
        );
    }

    static #compararAnos(numeroPesquisa, numeroProcesso, configuracaoPesquisa) {
        const campo = 'ano';
        return NumeroProcessoUtil.#compararCampo(
            numeroPesquisa,
            numeroProcesso,
            configuracaoPesquisa,
            campo,
            TAMANHOS_CAMPOS_NUMERO_PROCESSO[campo].maximo
        );
    }

    static #compararOrgaos(numeroPesquisa, numeroProcesso, configuracaoPesquisa) {
        const campo = 'orgao';
        return NumeroProcessoUtil.#compararCampo(
            numeroPesquisa,
            numeroProcesso,
            configuracaoPesquisa,
            campo,
            TAMANHOS_CAMPOS_NUMERO_PROCESSO[campo].maximo
        );
    }

    static #compararTribunais(numeroPesquisa, numeroProcesso, configuracaoPesquisa) {
        const campo = 'regional';
        return NumeroProcessoUtil.#compararCampo(
            numeroPesquisa,
            numeroProcesso,
            configuracaoPesquisa,
            campo,
            TAMANHOS_CAMPOS_NUMERO_PROCESSO[campo].maximo
        );
    }

    static #compararOrigens(numeroPesquisa, numeroProcesso, configuracaoPesquisa) {
        const campo = 'vara';
        return NumeroProcessoUtil.#compararCampo(
            numeroPesquisa,
            numeroProcesso,
            configuracaoPesquisa,
            campo,
            TAMANHOS_CAMPOS_NUMERO_PROCESSO[campo].maximo
        );
    }

    /**
     * Retorna um objeto com os dados do número do processo.
     *
     * @param {string} numeroProcesso Número do processo.
     * @param {boolean} completarOrigem Indica se o campo opcional origem (vara) deve ser completado com zeros, caso não exista.
     * @returns {Object} Um objeto com os dados do número do processo.
     */
    static getObjetoNumeroProcesso(numeroProcesso, completarOrigem = true) {
        if (TIPO_ESTRUTURA_NUMERO_PROCESSO.APENAS_NUMEROS.test(numeroProcesso)) {
            numeroProcesso = NumeroProcessoUtil.formatarNumeroProcessoApenasNumeros(numeroProcesso);
        }

        const resultado = ESTRUTURA_NUMERO_PROCESSO_COM_OU_SEM_CLASSE_PROCESSUAL.exec(numeroProcesso);

        if (resultado === null) {
            return new NumeroProcesso();
        }

        return NumeroProcessoUtil.#carregarObjNumeroProcesso(resultado, completarOrigem);
    }

    static #carregarObjNumeroProcesso(resultado, completarOrigem) {
        let objNumero = new NumeroProcesso();

        objNumero.classe = resultado?.groups?.classe ? resultado.groups.classe : '';
        objNumero.numero = resultado?.groups?.numero ? resultado.groups.numero : '';
        objNumero.digito = resultado?.groups?.digito ? resultado.groups.digito : '';
        objNumero.ano = resultado?.groups?.ano ? resultado.groups.ano : '';
        objNumero.orgao = resultado?.groups?.j
            ? resultado.groups.j
            : NumeroProcessoUtil.#extrairOrgao(resultado.groups.jtr);
        objNumero.regional = resultado?.groups?.tr
            ? NumeroProcessoUtil.completarZerosEsquerda(
                  resultado.groups.tr,
                  TAMANHOS_CAMPOS_NUMERO_PROCESSO.regional.maximo
              )
            : NumeroProcessoUtil.#extrairTribunal(resultado?.groups?.jtr);
        objNumero.vara = resultado?.groups?.origem
            ? NumeroProcessoUtil.completarZerosEsquerda(
                  resultado.groups.origem,
                  TAMANHOS_CAMPOS_NUMERO_PROCESSO.vara.maximo
              )
            : NumeroProcessoUtil.#tratarOrigem(completarOrigem);

        return objNumero;
    }

    static #extrairOrgao(jtr) {
        if (jtr === undefined) {
            return '';
        }

        return jtr.substring(0, 1);
    }

    static #extrairTribunal(jtr) {
        if (jtr === undefined) {
            return '';
        }

        return NumeroProcessoUtil.completarZerosEsquerda(
            jtr.substring(1),
            TAMANHOS_CAMPOS_NUMERO_PROCESSO.regional.maximo
        );
    }

    static #tratarOrigem(completarOrigem) {
        if (!completarOrigem) {
            return '';
        }

        return '0000';
    }

    static #tratarParametroNumeroProcesso(processoNumeroCompleto) {
        if (
            !processoNumeroCompleto ||
            typeof processoNumeroCompleto === 'boolean' ||
            typeof processoNumeroCompleto === 'symbol'
        ) {
            return '';
        }

        if (typeof processoNumeroCompleto === 'number' || typeof processoNumeroCompleto === 'bigint') {
            return '' + processoNumeroCompleto;
        }

        if (typeof processoNumeroCompleto === 'object') {
            return NumeroProcessoUtil.#validarObjNumeroProcesso(processoNumeroCompleto);
        }

        if (typeof processoNumeroCompleto !== 'string' && !(processoNumeroCompleto instanceof String)) {
            return '';
        }

        return processoNumeroCompleto;
    }

    static #validarObjNumeroProcesso(processoNumeroCompleto) {
        const numeroProcesso = new NumeroProcesso();
        const camposInexistentes = [];
        let existePeloMenosUm = false;

        for (const property in numeroProcesso) {
            if (property in processoNumeroCompleto) {
                existePeloMenosUm = true;
            } else {
                camposInexistentes.push(property);
            }
        }

        if (!existePeloMenosUm) {
            throw new TypeError(
                `Parâmetro inválido. Esperado um objeto com pelo menos 1 atributo do número do processo.`
            );
        }

        if (camposInexistentes.length > 0) {
            console.warn(
                `O objeto não contém os seguintes atributos do número do processo: ${camposInexistentes.join(', ')}.`
            );
        }

        return processoNumeroCompleto;
    }

    /**
     * Preenche um valor com zeros à esquerda.
     *
     * @param {string | number} texto O texto original.
     * @param {number} tamanho O tamanho final do texto.
     * @returns {string} O texto com o tamanho informado, com zeros à esquerda, caso o tamanho do texto original seja menor que o tamanho informado.
     */
    static completarZerosEsquerda(texto, tamanho) {
        if (texto) {
            return String(texto).padStart(tamanho, '0');
        }

        return String(texto);
    }

    /**
     * Valida se é um número inteiro.
     *
     * @param {number | string} n Número ou string numérica.
     * @returns {boolean} Um sinalizador que se é um número inteiro (true) ou não (false).
     */
    static isNumeroInteiro(n) {
        return /^\d+$/g.test(String(n));
    }
}
